type Success<T> = [error: null, data: T]
type Failure = [error: Error, data: null]
type Result<T> = Success<T> | Failure

/**
 * Normalises any thrown value into an `Error` instance.
 *
 * Handles four shapes in priority order:
 *  1. Already an Error - returned as-is
 *  2. A string - wrapped in a new Error
 *  3. An HTTP response-like object { status: number, statusText?, url?, data? }, such as Fetch Response and Axios error
 *     message built from status code, status text, URL, and an optional data.message
 *  4. A plain object with a message string property
 *  5. Anything else - Error('Unknown error')
 *
 *  1, 3, 4, 5 carry the original thrown value as `cause`; strings (2) do not.
 */
export const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error

  if (typeof error === 'string') return new Error(error)

  if (error && typeof error === 'object') {
    // HTTP response-like: { status: number, statusText?, url?, data? }
    const obj = error as { status?: unknown; statusText?: unknown; url?: unknown; data?: unknown }
    if (typeof obj.status === 'number') {
      const statusText = typeof obj.statusText === 'string' ? ` ${obj.statusText}` : ''
      const url = typeof obj.url === 'string' ? ` ${obj.url}` : ''
      const base = `HTTP ${obj.status}${statusText}${url}`
      const message =
        obj.data && typeof obj.data === 'object' && 'message' in obj.data
          ? `${base}: ${(obj.data as { message?: unknown }).message}`
          : base
      return new Error(message, { cause: error })
    }

    // Plain object with a message string
    const { message } = error as { message?: unknown }
    return new Error(typeof message === 'string' ? message : 'Unknown error', { cause: error })
  }

  return new Error('Unknown error', { cause: error })
}

/** Narrows `Result` to the success branch - for typescript */
export const isOk = <T>(result: Result<T>): result is Success<T> => result[0] === null

/**
 * Wraps a Promise or a synchronous function in a [error, data] tuple so callers never need a try/catch block.
 *
 * Async usage:
 *   const [err, user] = await tryCatch(fetchUser(id))
 *
 * Sync usage:
 *   const [err, parsed] = tryCatch(() => JSON.parse(raw))
 */
export function tryCatch<T>(input: () => T): Result<T>
export function tryCatch<T>(input: Promise<T>): Promise<Result<T>>
export function tryCatch<T>(input: Promise<T> | (() => T)): Promise<Result<T>> | Result<T> {
  if (typeof input === 'function') {
    try {
      return [null, input()]
    } catch (error) {
      return [normalizeError(error), null]
    }
  }

  return input.then(
    (data) => [null, data] as Result<T>,
    (error) => [normalizeError(error), null] as Result<T>,
  )
}
